import { Contents } from 'epubjs';
import { AdditionalStylesheets, BookViewMode } from '../types';
import documentFix, { TEXT_WIDTH_AFTER_VIEWPORT, TEXT_WIDTH_IN_VIEWPORT } from '../util/documentFix';
import { getEpubContainerElement } from '../util/book';
import { flattenToc } from '../util/toc';
import { NavItem } from 'epubjs/types/navigation';
import useUiStore from '../stores/ui';
import useRenditionStore from '../stores/rendition';

export type ElementVisibilityInfo =
  | {
      position: 'in-viewport';
      widthShareAfter?: number;
    }
  | {
      position: 'outside-viewport';
    };

const useRenderer = () => {
  // TODO make continuous specific implementation. Use view.visible property instead.
  const getReflowableElementVisibilityInfo = (elementId: string, targetDoc?: Document): ElementVisibilityInfo => {
    const epubContainer = getEpubContainerElement();
    if (!epubContainer) {
      throw new Error('Epub container not found');
    }

    let elementForText: HTMLElement | null = null;
    if (targetDoc) {
      elementForText = targetDoc.getElementById(elementId);
    } else {
      epubContainer.querySelectorAll('iframe').forEach((iframe) => {
        if (!elementForText) {
          elementForText = iframe.contentWindow!.document.getElementById(elementId);
        }
      });
    }

    if (elementForText) {
      const textSectionsWidths = documentFix.getTextSectionsWidthsRelativeToViewport(epubContainer, elementForText);

      // At least one text section is in the viewport

      // @ts-ignore
      if (textSectionsWidths[TEXT_WIDTH_IN_VIEWPORT]) {
        // At least one text section is after the viewport

        // @ts-ignore
        if (textSectionsWidths[TEXT_WIDTH_AFTER_VIEWPORT]) {
          return {
            position: 'in-viewport',

            // @ts-ignore
            widthShareAfter: textSectionsWidths[TEXT_WIDTH_AFTER_VIEWPORT],
          };
        }

        return {
          position: 'in-viewport',
        };
      }
    }

    return {
      position: 'outside-viewport',
    };
  };
  const addStylesheetCss = (contents: Contents, stylesheets: AdditionalStylesheets[] | undefined) => {
    if (stylesheets && stylesheets.length) {
      stylesheets.forEach((stylesheet) => {
        // This will add new stylesheet to every page(iframe) directly
        contents.addStylesheetCss(stylesheet.styles, stylesheet.key);
      });
    }
  };

  const addEventToIframeContents = <T extends Event>(
    contents: Contents,
    event: string,
    callback: (event: T) => void,
  ) => {
    const { firstChild } = contents.document;
    if (firstChild && `on${event}` in firstChild) {
      firstChild.addEventListener(event, (e: Event) => callback(e as T));
    }
  };

  const addTitleToIframe = (contents: Contents, toc: NavItem[], title: string) => {
    // This iframe element in both scroll and paginated modes
    const iframe = contents.document?.defaultView?.frameElement as HTMLIFrameElement | null;
    if (iframe && !iframe?.title) {
      const spineHref = contents?.document?.baseURI?.split('/').pop()?.split('#')[0] ?? '';

      // Flatten all TOC items including subitems
      const flatToc = flattenToc(toc);

      const tocItem = flatToc.find((nav) => nav.href?.includes(spineHref));

      const chapterLabel = tocItem?.label || spineHref || 'Untitled Section';
      iframe.setAttribute('title', `${title || 'Book'} — ${chapterLabel}`);
    }
  };

  /**
   * Unified theme and background application for both continuous and paginated modes.
   * This ensures consistent theme handling across all book types.
   *
   * @param contents - The iframe contents
   * @param viewMode - The current view mode (continuous or paginated)
   */
  const applyThemeAndBackground = (contents: Contents, viewMode: BookViewMode) => {
    const { themeMode } = useUiStore();
    const { setHasBackgroundOverride } = useRenditionStore();

    contents.document.body.classList.remove('light', 'dark');
    contents.document.body.classList.add(themeMode);

    // For continuous mode, set background color directly on body
    // This ensures both reflowable and fixed layout continuous books get proper backgrounds
    if (viewMode === 'continuous') {
      contents.document.body.style.backgroundColor = themeMode === 'dark' ? '#1A1F23' : '#F2F5F8';
    }

    // Track that we've overridden the background
    // This flag is used by audio annotations to determine blend mode
    setHasBackgroundOverride(true);
  };

  return {
    getReflowableElementVisibilityInfo,
    addStylesheetCss,
    addEventToIframeContents,
    addTitleToIframe,
    applyThemeAndBackground,
  };
};

export default useRenderer;
